import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  BookOpen, 
  Calendar, 
  Target, 
  CheckSquare, 
  MessageSquare,
  Settings,
  LogOut,
  Zap,
  Menu,
  X,
  ChevronRight,
  BarChart3,
  Users,
  Shield,
  Crown,
  CreditCard,
  Sun,
  Moon,
  Flag
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import BetaFeedback from './BetaFeedback';
import OnboardingTour from './OnboardingTour';

const Layout = ({ children }) => {
  const { user, logout, refreshUser } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Refresh user data on mount to get updated admin status
  useEffect(() => {
    if (refreshUser) {
      refreshUser();
    }
  }, []);

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/strategic-objectives', icon: Flag, label: 'Strategic Objectives', enterprise: true },
    { to: '/goals', icon: Target, label: 'Strategic Goals' },
    { to: '/tasks', icon: CheckSquare, label: 'Tasks' },
    { to: '/teams', icon: Users, label: 'Teams' },
    { to: '/prompts', icon: Calendar, label: 'Daily Prompts' },
    { to: '/scorecard', icon: BarChart3, label: 'Scorecard' },
    { to: '/modules', icon: BookOpen, label: 'Learning' },
    { to: '/coach', icon: MessageSquare, label: 'AI Coach' },
    { to: '/billing', icon: CreditCard, label: 'Billing' },
  ];

  // Add admin link if user is admin
  if (user?.is_admin) {
    navItems.push({ to: '/admin', icon: Shield, label: 'Admin', adminOnly: true });
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-[#F5F2EA]'}`}>
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className={`lg:hidden fixed top-4 left-4 z-50 p-2 rounded-xl shadow-md ${
          isDark 
            ? 'bg-[#111827] text-[#26C6DA] border border-[#1F2937]' 
            : 'bg-white text-[#0F5156] border border-[#EBE9E1]'
        }`}
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-screen w-64 backdrop-blur-xl z-40 transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isDark ? 'bg-[#0D0D1A]/95 border-r border-[#1F2937]' : 'bg-white/95 border-r border-[#EBE9E1]'}`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`p-6 border-b ${isDark ? 'border-[#1F2937]' : 'border-[#EBE9E1]'}`}>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#26C6DA] to-[#8B5CF6] flex items-center justify-center">
                <Zap className="w-5 h-5 text-black" />
              </div>
              <span className={`font-bold text-lg ${isDark ? 'text-white' : 'text-[#0F5156]'}`} style={{ fontFamily: 'Outfit' }}>
                Strategic Architect
              </span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                    isActive
                      ? isDark 
                        ? 'bg-[#26C6DA]/20 text-[#26C6DA] border border-[#26C6DA]/30'
                        : 'bg-[#0F5156]/10 text-[#0F5156] border border-[#0F5156]/30'
                      : isDark 
                        ? 'text-slate-400 hover:text-[#26C6DA] hover:bg-[#26C6DA]/10'
                        : 'text-slate-600 hover:text-[#0F5156] hover:bg-[#0F5156]/10'
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Theme Toggle & User section */}
          <div className={`p-4 border-t ${isDark ? 'border-[#1F2937]' : 'border-[#EBE9E1]'}`}>
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`w-full flex items-center gap-3 px-4 py-3 mb-3 rounded-xl font-medium transition-all ${
                isDark 
                  ? 'text-slate-400 hover:text-[#26C6DA] hover:bg-[#26C6DA]/10' 
                  : 'text-slate-600 hover:text-[#0F5156] hover:bg-[#0F5156]/10'
              }`}
              data-testid="theme-toggle-btn"
            >
              {isDark ? (
                <>
                  <Sun className="w-5 h-5" />
                  Light Mode
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5" />
                  Dark Mode
                </>
              )}
            </button>

            {/* User Info */}
            <div className="flex items-center gap-3 px-4 py-3 mb-2">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#26C6DA] to-[#8B5CF6] flex items-center justify-center text-black font-medium">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-[#1E293B]'}`}>{user?.name || 'User'}</p>
                  {user?.is_admin && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                </div>
                <div className="flex items-center gap-2">
                  <p className={`text-xs truncate ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{user?.email}</p>
                  {user?.plan && user.plan !== 'free' && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium uppercase ${
                      user.plan === 'enterprise' ? 'bg-purple-500/20 text-purple-400' : 'bg-[#26C6DA]/20 text-[#26C6DA]'
                    }`}>
                      {user.plan}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className={`w-full justify-start gap-3 px-4 py-3 rounded-xl touch-manipulation ${
                isDark 
                  ? 'text-slate-400 hover:text-red-400 hover:bg-red-500/10 active:bg-red-500/20' 
                  : 'text-slate-600 hover:text-red-500 hover:bg-red-50 active:bg-red-100'
              }`}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Beta Feedback Button */}
      <BetaFeedback />
      
      {/* Onboarding Tour for new users */}
      <OnboardingTour />
    </div>
  );
};

export default Layout;
